import { useMemo } from 'react';
import { ProductTier } from '@weave/schema-gen-ts/dist/shared/call-intelligence/plg/product.pb';
import { useCallIntelDemoShallowStore } from '@frontend/api-call-intel';
import { useCallIntelSubscription, useHasCallIntelAclAccess } from '@frontend/call-intel-core';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { PATIENT_INSIGHT_PERMISSION, PATIENT_INSIGHT_TEASER_FF } from '../constants';

interface UseCheckPatientInsightEligibilityProps {
  locationId?: string;
}

interface UseCheckPatientInsightEligibilityResult {
  showPatientInsight: boolean;
  showPatientInsightTeaser: boolean;
  isLoading: boolean;
  currentPlan: ProductTier;
}

export const useCheckPatientInsightEligibility = ({
  locationId,
}: UseCheckPatientInsightEligibilityProps): UseCheckPatientInsightEligibilityResult => {
  const { isDemoAccount } = useCallIntelDemoShallowStore('isDemoAccount');
  const { getFeatureFlagValue } = useAppFlagStore();
  const { selectedLocationIds: scopedLocationIds } = useAppScopeStore();

  const selectedLocationIds = useMemo(
    () => (locationId ? [locationId] : scopedLocationIds),
    [locationId, scopedLocationIds]
  );

  const { isCallIntelFreeTrialEligible, isCallIntelActive, currentPlan, isLoading } = useCallIntelSubscription({
    locationIds: selectedLocationIds,
    includeAiReceptionist: false,
  });

  const hasPermission = useHasCallIntelAclAccess([PATIENT_INSIGHT_PERMISSION], {
    locations: selectedLocationIds,
    allowDemoOverride: true,
  });
  const isTeaserEnabled = getFeatureFlagValue({
    flagName: PATIENT_INSIGHT_TEASER_FF,
    locationIds: selectedLocationIds,
  });

  const isActiveAndHasAccess = isCallIntelActive && hasPermission;
  const isEligibleForTrialTeaser =
    !isDemoAccount && !isActiveAndHasAccess && isTeaserEnabled && isCallIntelFreeTrialEligible;
  const showPatientInsight = isDemoAccount || isActiveAndHasAccess || isEligibleForTrialTeaser;

  return {
    isLoading,
    showPatientInsight,
    showPatientInsightTeaser: isEligibleForTrialTeaser,
    currentPlan: isDemoAccount ? ProductTier.PRODUCT_TIER_PRO : currentPlan,
  };
};
